import { ROLES, ROLE_HIERARCHY, PERMISSIONS, PERMISSION_MAP, RoleType, PermissionType } from '../constants/permissions';

class PermissionService {
  
  hasPermission(userRole: RoleType | string, permission: PermissionType | string): boolean {
    if (!userRole || !permission) return false;
    
    const allowedRoles = PERMISSION_MAP[permission as PermissionType];
    if (!allowedRoles) return false;
    
    return allowedRoles.includes(userRole as RoleType);
  }

  hasRoleLevel(userRole: RoleType | string, requiredRole: RoleType | string): boolean {
    if (!userRole || !requiredRole) return false;
    
    const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole as RoleType);
    const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole as RoleType);
    
    if (userRoleIndex === -1 || requiredRoleIndex === -1) return false;
    
    return userRoleIndex <= requiredRoleIndex;
  }

  isGlobalAdmin(userRole: RoleType | string): boolean {
    return userRole === ROLES.ADMIN_GLOBALE;
  }

  isAdmin(userRole: RoleType | string): boolean {
    return userRole === ROLES.ADMIN_GLOBALE || userRole === ROLES.ADMIN;
  }

  canManageAllClients(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.MANAGE_CLIENTS);
  }

  canManageUsers(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.MANAGE_USERS);
  }

  canManageChatbots(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.MANAGE_CHATBOTS);
  }

  canAccessGoogleIntegrations(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.GOOGLE_INTEGRATIONS);
  }

  canEnableGoogleFeatures(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.ENABLE_GOOGLE_FEATURES);
  }

  canConfigureGoogleOAuth(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.CONFIGURE_GOOGLE_OAUTH);
  }

  canAccessFlowBuilder(userRole: RoleType | string): boolean {
    return this.hasPermission(userRole, PERMISSIONS.FLOW_BUILDER);
  }

  canAccessClientResource(userRole: RoleType | string, userClientId: number | string, resourceClientId: number | string): boolean {
    if (this.isGlobalAdmin(userRole)) return true;
    
    return String(userClientId) === String(resourceClientId);
  }

  getPermissionsForRole(userRole: RoleType | string): PermissionType[] {
    const permissions: PermissionType[] = [];
    
    Object.entries(PERMISSION_MAP).forEach(([permission, allowedRoles]) => {
      if (allowedRoles.includes(userRole as RoleType)) {
        permissions.push(permission as PermissionType);
      }
    });
    
    return permissions;
  }

  isValidRole(role: string): boolean {
    return ROLE_HIERARCHY.includes(role as RoleType);
  }

  getRoleLevel(role: RoleType | string): number {
    return ROLE_HIERARCHY.indexOf(role as RoleType);
  }
}

const permissionService = new PermissionService();
export default permissionService;

export { PermissionService };
